import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ActivityFormModal from './ActivityFormModal';

function ActivitiesGroupPanel({
  group,
  activities,
  actionOptions,
  locked,
  onCreate,
  onUpdate,
  onDeactivate,
  saving,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [forceDelete, setForceDelete] = useState(false);

  const isOpinions = group === 'opinions';
  const title = isOpinions ? 'Opinions (hot takes & predictions)' : 'Challenge group';
  const help = isOpinions
    ? 'Dailies opinion cards — not Pulse arcade predictions. Predictions are “hot take about a future event” with options.'
    : 'Trivia / Super Over / Run Connect / Snapshot / quiz cards. Group FE pays from day-config when the whole group is done.';

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (activity) => {
    setEditing(activity);
    setModalOpen(true);
  };

  const handleSubmit = async (payload) => {
    if (editing?.id) {
      await onUpdate(editing.id, payload);
    } else {
      await onCreate(payload);
    }
    setModalOpen(false);
    setEditing(null);
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {help}
      </Alert>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">
          {title}{' '}
          <Chip size="small" label={`${activities.length} active`} sx={{ ml: 1 }} />
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add {isOpinions ? 'opinion' : 'challenge'}
        </Button>
      </Box>

      {activities.length === 0 ? (
        <Alert severity="warning" icon={false}>
          <Chip
            label={isOpinions ? 'No opinions scheduled' : 'No challenges scheduled'}
            color="warning"
            size="small"
            sx={{ mr: 1 }}
          />
          Empty group is allowed — day complete only requires non-empty groups.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Completion</TableCell>
              <TableCell>Action</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities
              .slice()
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              .map((activity) => (
                <TableRow key={activity.id}>
                  <TableCell>{activity.sort_order ?? '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={activity.activity_type} />
                  </TableCell>
                  <TableCell>{activity.title || '—'}</TableCell>
                  <TableCell>
                    {activity.completion_mode || 'participation'}
                    {activity.completion_mode === 'target'
                      ? ` · ${activity.target_value ?? 1}`
                      : ''}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" component="code">
                      {activity.action_key || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(activity)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setPendingDelete(activity);
                        setForceDelete(!!locked);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}

      <ActivityFormModal
        open={modalOpen}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditing(null);
          }
        }}
        onSubmit={handleSubmit}
        activity={editing}
        group={group}
        actionOptions={actionOptions}
        locked={locked}
        saving={saving}
      />

      <Dialog open={!!pendingDelete} onClose={() => !saving && setPendingDelete(null)}>
        <DialogTitle>Deactivate activity?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivate “{pendingDelete?.title}”? This hides it from the day. Progress is not
            deleted.
            {locked
              ? ' This day is locked — deactivation will send force_correction.'
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={async () => {
              await onDeactivate(pendingDelete.id, { forceCorrection: forceDelete });
              setPendingDelete(null);
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ActivitiesGroupPanel;
